import { canCreateTransactionType } from "../platform/access-control";
import { INITIAL_STOCK_MESSAGES, purchaseDateIssue } from "./chemical-initial-stock";
import { TRANSACTION_TYPE_METADATA, type TransactionType, isTransactionType } from "./transaction-types";
import { RECLASSIFICATION_COPY } from "./inventory-copy";

export const TRANSACTION_RECLASSIFICATIONS = {
  OPENING_BALANCE: ["PURCHASE"],
} as const satisfies Partial<Record<TransactionType, readonly TransactionType[]>>;

export const RECLASSIFICATION_MESSAGES = RECLASSIFICATION_COPY;

export type ReclassificationField = "supplierContactId" | "transactionDate";
type ReclassificationIssue = { field: ReclassificationField | "transactionType"; message: string };

export class TransactionReclassificationError extends Error {
  constructor(message: string, readonly field: ReclassificationIssue["field"]) {
    super(message);
    this.name = "TransactionReclassificationError";
  }
}

function listedTargets(from: string): readonly TransactionType[] {
  if (!isTransactionType(from)) return [];
  return (TRANSACTION_RECLASSIFICATIONS as Partial<Record<TransactionType, readonly TransactionType[]>>)[from] ?? [];
}

export function isReclassification(from: string, to: string): boolean {
  return listedTargets(from).some((target) => target === to);
}

export function getReclassificationTargets(from: string, role: string | null | undefined): TransactionType[] {
  return listedTargets(from).filter((to) =>
    canCreateTransactionType(role, to)
    && TRANSACTION_TYPE_METADATA[to].stockEffect === TRANSACTION_TYPE_METADATA[from as TransactionType].stockEffect);
}

export function reclassificationRequiredFields(to: TransactionType): ReclassificationField[] {
  const fields: ReclassificationField[] = [];
  if (TRANSACTION_TYPE_METADATA[to].supplierMode === "required") fields.push("supplierContactId");
  fields.push("transactionDate");
  return fields;
}

export function reclassificationIssues(
  input: {
    from: string;
    to: TransactionType;
    role: string | null | undefined;
    supplierContactId?: number | null;
    transactionDate?: string;
  },
  now = new Date(),
): ReclassificationIssue[] {
  if (!getReclassificationTargets(input.from, input.role).includes(input.to)) {
    return [{ field: "transactionType", message: RECLASSIFICATION_MESSAGES.notAllowed }];
  }
  const issues: ReclassificationIssue[] = [];
  const required = reclassificationRequiredFields(input.to);
  if (required.includes("supplierContactId") && !input.supplierContactId) {
    issues.push({ field: "supplierContactId", message: INITIAL_STOCK_MESSAGES.supplierRequired });
  }
  const dateMessage = purchaseDateIssue(input.transactionDate, now);
  if (dateMessage) issues.push({ field: "transactionDate", message: dateMessage });
  return issues;
}
